<template>
  <!-- Card 17trt6jd8uu: Pitch Coins do aluno e do professor. So leitura:
       saldo, como ganhar e o extrato em forma de caderneta. -->
  <div class="pcn">
    <page-skeleton v-if="loading" variant="finance" class="mt-4" />

    <p v-else-if="failed" class="pcn-empty mt-6">
      {{ $t("pitchCoins.loadError") }}
    </p>

    <template v-else>
      <p class="pcn__eyebrow">{{ $t("pitchCoins.eyebrow") }}</p>

      <section class="pcn-hero" :aria-label="$t('pitchCoins.title')">
        <div class="pcn-hero__balance">
          <!-- Retorno de 22/10: o saquinho de Pitch Coins no lugar do disco com o
               "P" (imagem enviada pelo cliente, sem o texto). -->
          <img
            class="pcn-medal"
            src="@/assets/images/connect/pitch-coin-bag.png"
            alt=""
            aria-hidden="true"
          />
          <div class="pcn-hero__figure">
            <span class="pcn-tag">{{ $t("pitchCoins.available") }}</span>
            <p
              class="pcn-hero__value"
              :class="{ 'pcn-hero__value--neg': summary.available < 0 }"
            >
              {{ formatCoins(summary.available) }}
              <span class="pcn-hero__unit">{{ $t("pitchCoins.unit") }}</span>
            </p>
            <p v-if="summary.available < 0" class="pcn-hero__note">
              {{ $t("pitchCoins.negative") }}
            </p>
          </div>
        </div>

        <dl class="pcn-stats">
          <div class="pcn-stat">
            <dt>{{ $t("pitchCoins.accumulated") }}</dt>
            <dd>{{ formatCoins(summary.accumulated) }}</dd>
          </div>
          <div class="pcn-stat">
            <dt>{{ $t("pitchCoins.used") }}</dt>
            <dd>{{ formatCoins(summary.used) }}</dd>
          </div>
          <div class="pcn-stat">
            <dt>{{ $t("pitchCoins.expired") }}</dt>
            <dd>{{ formatCoins(summary.expired) }}</dd>
          </div>
          <div v-if="summary.reserved > 0" class="pcn-stat">
            <dt>{{ $t("pitchCoins.reserved") }}</dt>
            <dd>{{ formatCoins(summary.reserved) }}</dd>
          </div>
          <div v-if="summary.pending > 0" class="pcn-stat">
            <dt>{{ $t("pitchCoins.pending") }}</dt>
            <dd>{{ formatCoins(summary.pending) }}</dd>
          </div>
        </dl>

        <p
          class="pcn-expiry"
          :class="{ 'pcn-expiry--warn': summary.expiringSoon > 0 }"
        >
          <span class="pcn-expiry__dot" aria-hidden="true"></span>
          <span>
            {{
              summary.expiringSoon > 0
                ? $t("pitchCoins.expiringSoon", {
                    amount: formatCoins(summary.expiringSoon),
                    days: summary.expiringSoonDays,
                  })
                : $t("pitchCoins.noneExpiring", {
                    days: summary.expiringSoonDays,
                  })
            }}
          </span>
        </p>
      </section>

      <!-- Retorno de 22/10: campanhas em andamento para este publico, em
           destaque (titulo, descricao, quanto vale e o prazo). -->
      <section
        v-if="campaigns.length"
        class="pcn-campaigns"
        :aria-label="$t('pitchCoins.campaigns')"
      >
        <article v-for="item in campaigns" :key="item.id" class="pcn-campaign">
          <span class="pcn-tag pcn-campaign__tag">{{
            $t("pitchCoins.campaignTag")
          }}</span>
          <h2 class="pcn-campaign__title">{{ item.name }}</h2>
          <p v-if="item.description" class="pcn-campaign__desc">
            {{ item.description }}
          </p>
          <p class="pcn-campaign__meta">
            <strong class="pcn-campaign__value">{{
              campaignValue(item)
            }}</strong>
            <span>{{
              $t("pitchCoins.campaignUntil", { date: lastDay(item.endsAt) })
            }}</span>
          </p>
        </article>
      </section>

      <!-- Retorno de 27/10: selos e ranking meio a meio, antes do resgate. -->
      <div class="pcn-duo">
        <!-- Card 17trt6jd8uu, etapa 4: selos. -->
        <badges-card class="pcn-badges" :profile="profile" />

        <!-- Card 17trt6jd8uu, etapa 4: ranking (alunos; professores no geral). -->
        <section v-if="ranking" class="pcn-rank" :aria-label="rankingTitle">
          <header class="pcn-section">
            <h2>{{ rankingTitle }}</h2>
            <p>
              {{ $t("pitchCoins.ranking.subtitle", { date: rankingEnd }) }}
            </p>
          </header>

          <div v-if="rankingTabs.length > 1" class="pcn-rank__tabs">
            <button
              v-for="tab in rankingTabs"
              :key="tab.id"
              type="button"
              class="pcn-refer__chip"
              :class="{ 'is-on': rankTab === tab.id }"
              @click="rankTab = tab.id"
            >
              {{ tab.label }}
            </button>
          </div>

          <ol v-if="rankingBoard.entries.length" class="pcn-rank__list">
            <li
              v-for="row in rankingBoard.entries"
              :key="row.position + row.name"
              class="pcn-rank__row"
              :class="{ 'is-me': row.isMe }"
            >
              <span class="pcn-rank__pos">
                <v-icon
                  v-if="row.medal"
                  size="22"
                  :color="medalColor(row.medal)"
                  :aria-label="$t('pitchCoins.ranking.medal', { n: row.medal })"
                >
                  mdi-medal
                </v-icon>
                <template v-else>{{ row.position }}º</template>
              </span>
              <span class="pcn-rank__name">
                {{ row.name }}
                <em v-if="row.isMe">{{ $t("pitchCoins.ranking.you") }}</em>
                <span v-if="(row.badges || []).length" class="pcn-rank__badges">
                  <badge-icon
                    v-for="badge in row.badges"
                    :key="badge.code"
                    :badge="badge"
                    :size="22"
                  />
                </span>
              </span>
              <span class="pcn-rank__score">{{ formatCoins(row.score) }}</span>
            </li>
            <li
              v-if="rankingBoard.me"
              class="pcn-rank__row pcn-rank__row--me is-me"
            >
              <span class="pcn-rank__pos">{{ rankingBoard.me.position }}º</span>
              <span class="pcn-rank__name">
                {{ rankingBoard.me.name }}
                <em>{{ $t("pitchCoins.ranking.you") }}</em>
              </span>
              <span class="pcn-rank__score">
                {{ formatCoins(rankingBoard.me.score) }}
              </span>
            </li>
          </ol>
          <p v-else class="pcn-empty">{{ $t("pitchCoins.ranking.empty") }}</p>
        </section>
      </div>

      <!-- Card 17trt6jd8uu, etapa 3: trocar coins por beneficios. Some
           enquanto a equipe nao cadastrar nenhum e a pessoa nao tiver pedidos. -->
      <section
        v-if="benefits.length || redemptions.length"
        class="pcn-rewards"
        :aria-label="$t('pitchCoins.rewards')"
      >
        <header class="pcn-section">
          <h2>{{ $t("pitchCoins.rewards") }}</h2>
          <p>{{ $t("pitchCoins.rewardsSub") }}</p>
        </header>

        <ul v-if="benefits.length" class="pcn-vouchers">
          <li v-for="benefit in benefits" :key="benefit.id" class="pcn-voucher">
            <div class="pcn-voucher__body">
              <span class="pcn-voucher__name">{{ benefit.name }}</span>
              <span v-if="discountText(benefit)" class="pcn-voucher__deal">
                {{ discountText(benefit) }}
              </span>
              <span v-if="benefit.description" class="pcn-voucher__desc">
                {{ benefit.description }}
              </span>
              <span class="pcn-voucher__meta">
                <span v-if="benefit.endsAt">
                  {{
                    $t("pitchCoins.until", { date: lastDay(benefit.endsAt) })
                  }}
                </span>
                <span v-if="benefit.stockLeft !== null">
                  {{ $t("pitchCoins.left", { count: benefit.stockLeft }) }}
                </span>
              </span>
            </div>
            <div class="pcn-voucher__stub">
              <span class="pcn-voucher__cost">
                {{
                  $t("pitchCoins.cost", { amount: formatCoins(benefit.cost) })
                }}
              </span>
              <button
                type="button"
                class="pcn-voucher__btn"
                :disabled="!canRequest(benefit)"
                @click="ask(benefit)"
              >
                {{ requestLabel(benefit) }}
              </button>
            </div>
          </li>
        </ul>

        <template v-if="redemptions.length">
          <h3 class="pcn-subtitle">{{ $t("pitchCoins.myRedemptions") }}</h3>
          <ul class="pcn-requests">
            <li v-for="item in redemptions" :key="item.id" class="pcn-request">
              <div class="pcn-request__main">
                <span class="pcn-request__name">{{ item.benefit.name }}</span>
                <span class="pcn-request__meta">
                  {{ fullDate(item.createdAt) }} ·
                  {{
                    $t("pitchCoins.cost", { amount: formatCoins(item.cost) })
                  }}
                </span>
                <span
                  v-if="item.status === 'rejected' && item.decisionReason"
                  class="pcn-request__note"
                >
                  {{ $t("pitchCoins.reason", { reason: item.decisionReason }) }}
                </span>
                <span v-if="item.discount" class="pcn-request__discount">
                  <template v-if="item.discount.applied > 0">
                    {{
                      $t("pitchCoins.discountApplied", {
                        amount: money(item.discount.applied),
                        months: chargeMonths(item.discount.charges),
                      })
                    }}
                  </template>
                  <template v-if="item.discount.pending > 0">
                    <template v-if="item.discount.applied > 0"> · </template>
                    {{
                      $t("pitchCoins.discountWaiting", {
                        amount: money(item.discount.pending),
                      })
                    }}
                  </template>
                </span>
              </div>
              <div class="pcn-request__side">
                <span class="pcn-pill" :class="`pcn-pill--${item.status}`">
                  {{ $t(`pitchCoins.redemptionStatus.${item.status}`) }}
                </span>
                <button
                  v-if="item.status === 'pending'"
                  type="button"
                  class="pcn-link"
                  :disabled="cancelling === item.id"
                  @click="cancelRequest(item)"
                >
                  {{ $t("pitchCoins.cancelRequest") }}
                </button>
              </div>
            </li>
          </ul>
        </template>
      </section>

      <!-- Card 17trt6jd8uu, etapa 5: indicacoes. -->
      <section class="pcn-refer" :aria-label="$t('pitchCoins.refer')">
        <header class="pcn-section">
          <h2>{{ $t("pitchCoins.refer") }}</h2>
          <p>
            {{
              profile === "partner"
                ? $t("pitchCoins.referSubPartner")
                : $t("pitchCoins.referSubCustomer")
            }}
          </p>
        </header>

        <form class="pcn-refer__form" @submit.prevent="sendReferral">
          <div
            v-if="profile === 'partner'"
            class="pcn-refer__kind"
            role="radiogroup"
          >
            <button
              v-for="option in ['student', 'teacher']"
              :key="option"
              type="button"
              role="radio"
              class="pcn-refer__chip"
              :class="{ 'is-on': referTarget === option }"
              :aria-checked="referTarget === option ? 'true' : 'false'"
              @click="referTarget = option"
            >
              {{
                option === "student"
                  ? $t("pitchCoins.referStudent")
                  : $t("pitchCoins.referTeacher")
              }}
            </button>
          </div>
          <label class="pcn-refer__field">
            <span class="sr-only">{{ $t("pitchCoins.referEmail") }}</span>
            <input
              v-model.trim="referEmail"
              type="email"
              autocomplete="off"
              :placeholder="$t('pitchCoins.referEmail')"
            />
          </label>
          <button
            type="submit"
            class="pcn-voucher__btn"
            :disabled="!referEmail || sendingReferral"
          >
            {{ $t("pitchCoins.referSend") }}
          </button>
        </form>

        <template v-if="referrals.length">
          <h3 class="pcn-subtitle">{{ $t("pitchCoins.myReferrals") }}</h3>
          <ul class="pcn-requests">
            <li v-for="item in referrals" :key="item.id" class="pcn-request">
              <div class="pcn-request__main">
                <span class="pcn-request__name">{{ item.email }}</span>
                <span class="pcn-request__meta">
                  {{ $t(`pitchCoins.referralKinds.${item.kind}`) }} ·
                  {{ fullDate(item.createdAt) }}
                </span>
                <span v-if="item.pending" class="pcn-request__note">
                  {{ $t(`pitchCoins.referralPending.${item.pending}`) }}
                </span>
              </div>
              <span
                class="pcn-pill"
                :class="{
                  'pcn-pill--approved': item.status === 'rewarded',
                  'pcn-pill--pending': item.status === 'registered',
                }"
              >
                {{ $t(`pitchCoins.referralStatus.${item.status}`) }}
              </span>
            </li>
          </ul>
        </template>
      </section>

      <div class="pcn-grid">
        <!-- Caderneta: uma linha por movimentacao, com o saldo depois dela. -->
        <section class="pcn-book" :aria-label="$t('pitchCoins.statement')">
          <header class="pcn-section">
            <h2>{{ $t("pitchCoins.statement") }}</h2>
            <p>{{ $t("pitchCoins.statementSub") }}</p>
          </header>

          <p v-if="!lines.length" class="pcn-empty">
            {{ $t("pitchCoins.emptyStatement") }}
          </p>

          <ol v-else class="pcn-lines">
            <li
              v-for="line in lines"
              :key="line.id"
              class="pcn-line"
              :class="{ 'pcn-line--muted': line.status !== 'confirmed' }"
            >
              <span class="pcn-stamp" aria-hidden="true">
                <span class="pcn-stamp__day">{{ day(line.createdAt) }}</span>
                <span class="pcn-stamp__month">{{
                  month(line.createdAt)
                }}</span>
              </span>
              <span class="pcn-line__body">
                <span class="pcn-line__row">
                  <span class="pcn-line__title">{{ title(line) }}</span>
                  <span class="pcn-line__leader" aria-hidden="true"></span>
                  <span class="pcn-line__amount" :class="amountClass(line)">
                    {{ amountText(line) }}
                  </span>
                </span>
                <span class="pcn-line__meta">
                  <span class="sr-only">{{ fullDate(line.createdAt) }}</span>
                  <span>{{ kindLabel(line) }}</span>
                  <span v-if="statusText(line)"> · {{ statusText(line) }}</span>
                  <span v-if="line.kind === 'credit' && line.expiresAt">
                    ·
                    {{
                      $t("pitchCoins.validUntil", {
                        date: fullDate(line.expiresAt),
                      })
                    }}
                  </span>
                  <span class="pcn-line__balance">
                    {{
                      $t("pitchCoins.balanceAfter", {
                        amount: formatCoins(line.balanceAfter),
                      })
                    }}
                  </span>
                </span>
                <span v-if="line.reason" class="pcn-line__reason">
                  {{ line.reason }}
                </span>
              </span>
            </li>
          </ol>

          <div v-if="lines.length < total" class="pcn-more">
            <button
              type="button"
              class="pcn-more__btn"
              :disabled="loadingMore"
              @click="loadMore"
            >
              {{ $t("pitchCoins.loadMore") }}
            </button>
          </div>
        </section>

        <section class="pcn-earn" :aria-label="$t('pitchCoins.howToEarn')">
          <header class="pcn-section">
            <h2>{{ $t("pitchCoins.howToEarn") }}</h2>
            <p>{{ $t("pitchCoins.howToEarnSub") }}</p>
          </header>

          <p v-if="!guide.length" class="pcn-empty">
            {{ $t("pitchCoins.noRules") }}
          </p>

          <ul v-else class="pcn-rules">
            <li v-for="rule in guide" :key="rule.id" class="pcn-rule">
              <div class="pcn-rule__head">
                <span class="pcn-rule__name">{{ rule.name }}</span>
                <span
                  class="pcn-rule__points"
                  :class="{ 'pcn-rule__points--neg': rule.points < 0 }"
                >
                  {{ formatCoins(rule.points, { signed: true }) }}
                </span>
              </div>
              <p v-if="rule.description" class="pcn-rule__desc">
                {{ rule.description }}
              </p>
              <p class="pcn-rule__meta">
                <span v-if="rule.mode === 'manual'">{{
                  $t("pitchCoins.manual")
                }}</span>
                <span v-else-if="!rule.monthlyLimit">{{
                  $t("pitchCoins.perTime")
                }}</span>
                <span v-if="rule.campaign" class="pcn-rule__campaign">
                  {{ $t("pitchCoins.campaign", { name: rule.campaign }) }}
                </span>
                <!-- Retorno de 22/10: prazo 0 nao aparece. Number() porque a
                     coluna inteira ja chegou como string "0" (verdadeira). -->
                <span v-if="Number(rule.releaseDays) > 0">
                  {{ $t("pitchCoins.releaseDays", { days: rule.releaseDays }) }}
                </span>
              </p>
              <div v-if="rule.monthlyLimit" class="pcn-limit">
                <span
                  class="pcn-limit__bar"
                  role="progressbar"
                  :aria-valuenow="rule.usedThisMonth"
                  aria-valuemin="0"
                  :aria-valuemax="rule.monthlyLimit"
                  :aria-label="limitText(rule)"
                >
                  <span
                    class="pcn-limit__fill"
                    :style="{ width: `${limitPercent(rule)}%` }"
                  ></span>
                </span>
                <span class="pcn-limit__text">{{ limitText(rule) }}</span>
              </div>
            </li>
          </ul>
        </section>
      </div>
    </template>

    <!-- Confirmacao do pedido. O v-dialog fica fora da pagina: as cores
         usam os tokens globais direto, nao as variaveis de .pcn. -->
    <v-dialog v-model="confirmOpen" max-width="440">
      <div v-if="confirming" class="pcn-confirm">
        <h3 class="pcn-confirm__title">
          {{ $t("pitchCoins.confirmTitle", { name: confirming.name }) }}
        </h3>
        <p class="pcn-confirm__text">
          {{
            $t("pitchCoins.confirmText", {
              amount: formatCoins(confirming.cost),
            })
          }}
        </p>
        <div class="pcn-confirm__actions">
          <button
            type="button"
            class="pcn-confirm__ghost"
            @click="confirmOpen = false"
          >
            {{ $t("pitchCoins.cancel") }}
          </button>
          <button
            type="button"
            class="pcn-confirm__primary"
            :disabled="requesting"
            @click="confirmRequest"
          >
            {{ $t("pitchCoins.confirm") }}
          </button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import pitchCustomerApi from "@/shared/pitchCustomerApi";
import pitchPartnerApi from "@/shared/pitchPartnerApi";
import { formatCoinDate, formatCoins } from "@/shared/coins";
import { formatBRL } from "@/plugins/i18n";
import BadgesCard from "@/components/coins/BadgesCard.vue";
import BadgeIcon from "@/components/coins/BadgeIcon.vue";

const PAGE_SIZE = 20;

export default {
  name: "PitchCoinsPage",

  components: { BadgesCard, BadgeIcon },

  props: {
    profile: {
      type: String,
      required: true,
      validator: (value) => ["customer", "partner"].includes(value),
    },
  },

  data() {
    return {
      loading: true,
      failed: false,
      loadingMore: false,
      summary: {},
      guide: [],
      lines: [],
      total: 0,
      page: 1,
      benefits: [],
      redemptions: [],
      confirmOpen: false,
      confirming: null,
      requesting: false,
      cancelling: null,
      referrals: [],
      campaigns: [],
      ranking: null,
      rankTab: "general",
      referEmail: "",
      referTarget: "student",
      sendingReferral: false,
    };
  },

  computed: {
    rankingTitle() {
      return this.$t(
        this.profile === "partner"
          ? "pitchCoins.ranking.titleTeachers"
          : "pitchCoins.ranking.title"
      );
    },
    rankingTabs() {
      if (!this.ranking) return [];
      return [
        { id: "general", label: this.$t("pitchCoins.ranking.general") },
        ...this.ranking.byTeacher.map((item) => ({
          id: item.teacherId,
          label: this.$t("pitchCoins.ranking.ofTeacher", {
            name: item.teacherName,
          }),
        })),
      ];
    },
    rankingBoard() {
      const empty = { entries: [], me: null };
      if (!this.ranking) return empty;
      if (this.rankTab === "general") return this.ranking.general;
      return (
        this.ranking.byTeacher.find(
          (item) => item.teacherId === this.rankTab
        ) || empty
      );
    },
    rankingEnd() {
      if (!this.ranking) return "";
      return formatCoinDate(
        new Date(new Date(this.ranking.period.endsAt).getTime() - 1),
        { day: "2-digit", month: "2-digit", year: "numeric" }
      );
    },
    api() {
      return this.profile === "partner" ? pitchPartnerApi : pitchCustomerApi;
    },
  },

  mounted() {
    this.load();
  },

  methods: {
    formatCoins,

    load() {
      this.loading = true;
      this.failed = false;
      Promise.all([
        this.api.getCoinsSummary(),
        this.api.getCoinsEarnGuide(),
        this.api.getCoinsStatement({ page: 1, size: PAGE_SIZE }),
        // Beneficios e pedidos nao derrubam a pagina se falharem.
        this.api.getCoinBenefits().catch(() => null),
        this.api.getCoinRedemptions().catch(() => null),
        this.api.getCoinReferrals().catch(() => null),
        this.api.getCoinCampaigns().catch(() => null),
        this.api.getCoinRanking().catch(() => null),
      ])
        .then(
          ([
            summary,
            guide,
            statement,
            benefits,
            redemptions,
            referrals,
            campaigns,
            ranking,
          ]) => {
            this.ranking = ranking?.data?.data || null;
            this.referrals = referrals?.data?.data || [];
            this.campaigns = campaigns?.data?.data || [];
            this.summary = summary.data?.data || {};
            this.guide = guide.data?.data || [];
            this.benefits = benefits?.data?.data || [];
            this.redemptions = redemptions?.data?.data || [];
            this.lines = statement.data?.data || [];
            this.total = Number(statement.data?.total || 0);
            this.page = 1;
          }
        )
        .catch(() => {
          this.failed = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    medalColor(medal) {
      return { 1: "#E0A800", 2: "#9AA3AD", 3: "#B5651D" }[medal];
    },

    money(value) {
      return formatBRL(value);
    },

    lastDay(endsAt) {
      return this.fullDate(new Date(new Date(endsAt).getTime() - 1));
    },

    // Pontuacao fixa: quantos coins vale. Multiplicador: o fator e as acoes.
    campaignValue(item) {
      if (item.kind === "multiplier") {
        return this.$t("pitchCoins.campaignMultiplier", {
          factor: formatCoins(item.value),
          rules: item.rules.join(", "),
        });
      }
      return this.$t("pitchCoins.campaignFixed", {
        amount: formatCoins(item.value),
      });
    },

    discountText(benefit) {
      if (benefit.kind !== "discount" || !benefit.discountAmount) return "";
      return this.$t(
        this.profile === "partner"
          ? "pitchCoins.discountFee"
          : "pitchCoins.discountCharge",
        { amount: this.money(benefit.discountAmount) }
      );
    },

    canRequest(benefit) {
      return (
        benefit.available && Number(this.summary.available || 0) >= benefit.cost
      );
    },

    requestLabel(benefit) {
      if (!benefit.available) return this.$t("pitchCoins.unavailable");
      const missing = benefit.cost - Number(this.summary.available || 0);
      if (missing > 0) {
        return this.$t("pitchCoins.needMore", { amount: formatCoins(missing) });
      }
      return this.$t("pitchCoins.request");
    },

    chargeMonths(charges) {
      return charges
        .filter((charge) => charge.referenceMonth)
        .map((charge) =>
          formatCoinDate(
            new Date(charge.referenceYear, charge.referenceMonth - 1, 15),
            { month: "short", year: "numeric" }
          ).replace(".", "")
        )
        .join(", ");
    },

    sendReferral() {
      const email = this.referEmail;
      this.sendingReferral = true;
      this.api
        .createCoinReferral(
          email,
          this.profile === "partner" ? this.referTarget : "student"
        )
        .then((res) => {
          this.referrals = res.data?.data || this.referrals;
          this.referEmail = "";
          this.$root.$emit("display-alert", {
            text: this.$t("pitchCoins.referSent", { email }),
            type: 0,
          });
        })
        .catch((err) => {
          this.$root.$emit("display-alert", {
            text:
              err?.response?.data?.message || this.$t("pitchCoins.referError"),
            type: 1,
          });
        })
        .finally(() => {
          this.sendingReferral = false;
        });
    },

    ask(benefit) {
      this.confirming = benefit;
      this.confirmOpen = true;
    },

    confirmRequest() {
      this.requesting = true;
      this.api
        .requestCoinRedemption(this.confirming.id)
        .then(() => {
          this.confirmOpen = false;
          this.$root.$emit("display-alert", {
            text: this.$t("pitchCoins.requested"),
            type: 0,
          });
          this.load();
        })
        .catch((err) => {
          this.$root.$emit("display-alert", {
            text:
              err?.response?.data?.message ||
              this.$t("pitchCoins.requestError"),
            type: 1,
          });
        })
        .finally(() => {
          this.requesting = false;
        });
    },

    cancelRequest(item) {
      this.cancelling = item.id;
      this.api
        .cancelCoinRedemption(item.id)
        .then(() => {
          this.$root.$emit("display-alert", {
            text: this.$t("pitchCoins.cancelled"),
            type: 0,
          });
          this.load();
        })
        .catch(() => {
          this.$root.$emit("display-alert", {
            text: this.$t("pitchCoins.cancelError"),
            type: 1,
          });
        })
        .finally(() => {
          this.cancelling = null;
        });
    },

    loadMore() {
      this.loadingMore = true;
      this.api
        .getCoinsStatement({ page: this.page + 1, size: PAGE_SIZE })
        .then((res) => {
          const known = new Set(this.lines.map((line) => line.id));
          const next = (res.data?.data || []).filter(
            (line) => !known.has(line.id)
          );
          this.lines = [...this.lines, ...next];
          this.total = Number(res.data?.total || this.total);
          this.page += 1;
        })
        .catch(() => {
          this.$root.$emit("display-alert", {
            text: this.$t("pitchCoins.loadError"),
            type: 1,
          });
        })
        .finally(() => {
          this.loadingMore = false;
        });
    },

    day(value) {
      return formatCoinDate(value, { day: "2-digit" });
    },
    month(value) {
      return formatCoinDate(value, { month: "short" }).replace(".", "");
    },
    fullDate(value) {
      return formatCoinDate(value, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },

    kindLabel(line) {
      return this.$t(`pitchCoins.kinds.${line.kind}`);
    },

    // O nome da acao quando houver; ajuste e importacao tem texto proprio.
    title(line) {
      if (line.rule?.code === "imported_balance") {
        return this.$t("pitchCoins.imported");
      }
      if (line.rule?.name) return line.rule.name;
      // Lancamento por campanha (sem regra): o nome da campanha.
      if (line.campaign?.name) return line.campaign.name;
      if (line.origin === "manual") return this.$t("pitchCoins.adjustment");
      return this.kindLabel(line);
    },

    statusText(line) {
      if (line.status === "skipped") {
        const reason = line.skipReason
          ? this.$t(`pitchCoins.skipReasons.${line.skipReason}`)
          : "";
        return reason || this.$t("pitchCoins.status.skipped");
      }
      if (line.status === "pending" || line.status === "cancelled") {
        return this.$t(`pitchCoins.status.${line.status}`);
      }
      return "";
    },

    amountText(line) {
      if (line.status === "skipped") return "0";
      // Resgate aprovado e vencimento de coins reservados nao mexem no
      // disponivel, mas a quantidade conta a historia da linha.
      if (line.amount === 0 && line.quantity > 0) {
        return formatCoins(line.quantity);
      }
      return formatCoins(line.amount, { signed: true });
    },

    amountClass(line) {
      if (line.amount > 0) return "is-gain";
      if (line.amount < 0)
        return line.kind === "expire" ? "is-expire" : "is-loss";
      return "";
    },

    limitPercent(rule) {
      if (!rule.monthlyLimit) return 0;
      return Math.min(
        100,
        (100 * (rule.usedThisMonth || 0)) / rule.monthlyLimit
      );
    },

    limitText(rule) {
      if ((rule.usedThisMonth || 0) >= rule.monthlyLimit) {
        return this.$t("pitchCoins.monthlyDone");
      }
      return this.$t("pitchCoins.monthlyProgress", {
        used: rule.usedThisMonth || 0,
        limit: rule.monthlyLimit,
      });
    },
  },
};
</script>

<style scoped>
.pcn {
  --ink: var(--dk-ink, #231a2e);
  --muted: var(--dk-muted, #6b6472);
  --faint: var(--dk-faint, #9a93a3);
  --line: var(--dk-line, #ece6ef);
  --surface: var(--dk-surface, #fff);
  --paper: var(--dk-paper, #fbf7fa);
  --pink: var(--dk-pink, #e8276f);
  --grape: var(--dk-grape, #a826e1);
  --gain: var(--dk-teal-deep, #00805f);
  --loss: var(--dk-red, #c62f3d);
  --ember: var(--dk-ember-deep, #b4560f);
  padding: 8px 4px 40px;
  color: var(--ink);
  font-family: "Poppins", sans-serif;
}

.pcn__eyebrow {
  margin: 0 0 12px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--grape);
}

.pcn-tag {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
}

/* ---------- heroi ---------- */
.pcn-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 18px 32px;
  padding: 26px 28px 20px;
  border-radius: 24px;
  border: 1px solid var(--line);
  background: radial-gradient(
      110% 150% at 0% 0%,
      rgba(168, 38, 225, 0.09),
      transparent 55%
    ),
    var(--surface);
  box-shadow: 0 22px 48px -38px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.55);
}

.pcn-hero__balance {
  display: flex;
  align-items: center;
  gap: 20px;
  min-width: 0;
}

/* O saquinho de Pitch Coins (imagem do cliente, mais alta que larga). */
.pcn-medal {
  flex-shrink: 0;
  width: 56px;
  height: auto;
  filter: drop-shadow(0 10px 14px rgba(168, 38, 225, 0.22));
}

.pcn-hero__figure {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.pcn-hero__value {
  margin: 6px 0 0;
  font-size: clamp(2.4rem, 6vw, 3.4rem);
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: var(--ink);
}

.pcn-hero__value--neg {
  color: var(--loss);
}

.pcn-hero__unit {
  margin-left: 4px;
  font-size: 1rem;
  font-weight: 600;
  color: var(--muted);
}

.pcn-hero__note {
  margin: 10px 0 0;
  max-width: 46ch;
  font-size: 0.82rem;
  color: var(--loss);
}

.pcn-stats {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(84px, auto);
  gap: 0;
  align-self: center;
  margin: 0;
}

.pcn-stat {
  padding: 4px 18px;
  border-left: 1px solid var(--line);
}

.pcn-stat dt {
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--muted);
}

.pcn-stat dd {
  margin: 4px 0 0;
  font-size: 1.2rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--ink);
}

.pcn-expiry {
  grid-column: 1 / -1;
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin: 0;
  padding-top: 14px;
  border-top: 1px solid var(--line);
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--ink);
}

.pcn-expiry__dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--gain);
  transform: translateY(-1px);
}

.pcn-expiry--warn .pcn-expiry__dot {
  background: var(--ember);
}

.pcn-expiry--warn {
  color: var(--ember);
}

/* ---------- trocar coins ---------- */
/* ---------- campanhas em destaque ---------- */
.pcn-campaigns {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 14px;
  margin-top: 22px;
}

.pcn-campaign {
  padding: 18px 22px;
  border-radius: 22px;
  border: 1px solid var(--grape);
  background: var(--paper);
  min-width: 0;
}

.pcn-campaign__tag {
  color: var(--grape);
}

.pcn-campaign__title {
  margin: 4px 0 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--ink);
  overflow-wrap: anywhere;
}

.pcn-campaign__desc {
  margin: 6px 0 0;
  font-size: 0.85rem;
  color: var(--muted);
  white-space: pre-line;
  overflow-wrap: anywhere;
}

.pcn-campaign__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 12px;
  margin: 12px 0 0;
  font-size: 0.8rem;
  color: var(--muted);
}

.pcn-campaign__value {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--pink);
}

.pcn-rewards {
  margin-top: 22px;
  padding: 22px 24px 20px;
  border-radius: 22px;
  border: 1px solid var(--line);
  background: var(--surface);
}

.pcn-vouchers {
  list-style: none;
  margin: 14px 0 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
}

/* O vale: corpo e canhoto separados por um picote, com os dois entalhes. */
.pcn-voucher {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  border-radius: 16px;
  border: 1px solid var(--line);
  background: var(--paper);
  overflow: hidden;
}

.pcn-voucher__body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 16px;
  min-width: 0;
}

.pcn-voucher__name {
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--ink);
}

.pcn-voucher__deal {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--pink);
}

.pcn-voucher__desc,
.pcn-voucher__meta {
  font-size: 0.75rem;
  color: var(--muted);
}

.pcn-voucher__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0 10px;
}

.pcn-voucher__stub {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 16px;
  border-left: 2px dashed var(--line);
  background: var(--surface);
}

.pcn-voucher__stub::before,
.pcn-voucher__stub::after {
  content: "";
  position: absolute;
  left: -9px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--surface);
  border: 1px solid var(--line);
}

.pcn-voucher__stub::before {
  top: -9px;
}

.pcn-voucher__stub::after {
  bottom: -9px;
}

.pcn-voucher__cost {
  font-size: 0.85rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--grape);
  white-space: nowrap;
}

.pcn-voucher__btn {
  padding: 7px 16px;
  border: 0;
  border-radius: 999px;
  background: linear-gradient(150deg, var(--pink), var(--grape));
  color: #fff;
  font: inherit;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.pcn-voucher__btn:focus-visible {
  outline: 2px solid var(--grape);
  outline-offset: 2px;
}

.pcn-voucher__btn:disabled {
  background: var(--line);
  color: var(--muted);
  cursor: not-allowed;
}

.pcn-subtitle {
  margin: 20px 0 0;
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--ink);
}

.pcn-requests {
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
}

.pcn-request {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  border-top: 1px dashed var(--line);
}

.pcn-request:first-child {
  border-top: 0;
}

.pcn-request__main {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.pcn-request__name {
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--ink);
}

.pcn-request__meta,
.pcn-request__note {
  font-size: 0.75rem;
  color: var(--muted);
}

.pcn-request__discount {
  margin-top: 3px;
  font-size: 0.78rem;
  color: var(--gain);
}

.pcn-request__side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  flex-shrink: 0;
}

.pcn-pill {
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
  background: var(--paper);
  color: var(--muted);
}

.pcn-pill--pending {
  background: rgba(180, 86, 15, 0.12);
  color: var(--ember);
}

.pcn-pill--approved {
  background: rgba(0, 128, 95, 0.12);
  color: var(--gain);
}

.pcn-pill--rejected {
  background: rgba(198, 47, 61, 0.1);
  color: var(--loss);
}

.pcn-link {
  border: 0;
  background: transparent;
  padding: 0;
  color: var(--muted);
  font: inherit;
  font-size: 0.75rem;
  text-decoration: underline;
  cursor: pointer;
}

.pcn-link:disabled {
  opacity: 0.5;
  cursor: progress;
}

/* ---------- indicar ---------- */
.pcn-duo {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 22px;
  margin-top: 22px;
  align-items: stretch;
}

/* Dentro da dupla o espaçamento é o do grid, e os dois cartões têm a mesma
   altura. */
.pcn-duo > .pcn-rank,
.pcn-duo > .pcn-badges {
  margin: 0;
  height: 100%;
  box-sizing: border-box;
}

/* Sem ranking (ou sem selos) o cartão que sobra ocupa a linha inteira. */
.pcn-duo > :only-child {
  grid-column: 1 / -1;
}

.pcn-rank {
  margin-top: 22px;
  padding: 22px 24px 20px;
  border-radius: 22px;
  border: 1px solid var(--line);
  background: var(--paper);
}

.pcn-rank__tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 14px;
}

.pcn-rank__list {
  list-style: none;
  margin: 14px 0 0;
  padding: 0;
}

.pcn-rank__row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 14px;
}

.pcn-rank__row + .pcn-rank__row {
  margin-top: 4px;
}

.pcn-rank__row.is-me {
  background: var(--surface);
  border: 1px solid var(--grape);
}

.pcn-rank__row--me {
  margin-top: 10px;
}

.pcn-rank__pos {
  flex: 0 0 34px;
  text-align: center;
  font-weight: 700;
  color: var(--muted);
}

.pcn-rank__name {
  flex: 1 1 auto;
  min-width: 0;
  font-weight: 600;
  color: var(--ink);
  overflow-wrap: anywhere;
}

.pcn-rank__name em {
  margin-left: 6px;
  font-style: normal;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--grape);
}

.pcn-rank__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.pcn-badges {
  margin: 22px 0 0;
}

.pcn-rank__score {
  font-weight: 700;
  color: var(--pink);
}

.pcn-refer {
  margin-top: 22px;
  padding: 22px 24px 20px;
  border-radius: 22px;
  border: 1px solid var(--line);
  background: var(--paper);
}

.pcn-refer__form {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
}

.pcn-refer__kind {
  display: inline-flex;
  padding: 3px;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: var(--surface);
}

.pcn-refer__chip {
  padding: 6px 14px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--muted);
  font: inherit;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
}

.pcn-refer__chip.is-on {
  background: var(--grape);
  color: #fff;
}

.pcn-refer__chip:focus-visible {
  outline: 2px solid var(--grape);
  outline-offset: 2px;
}

.pcn-refer__field {
  flex: 1 1 240px;
}

.pcn-refer__field input {
  width: 100%;
  padding: 9px 14px;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--ink);
  font: inherit;
  font-size: 0.88rem;
}

.pcn-refer__field input:focus-visible {
  outline: 2px solid var(--grape);
  outline-offset: 1px;
}

/* Dialogo: fora de .pcn, usa os tokens globais do tema. */
.pcn-confirm {
  padding: 24px;
  border-radius: 20px;
  background: var(--dk-surface, #fff);
  color: var(--dk-ink, #231a2e);
  font-family: "Poppins", sans-serif;
}

.pcn-confirm__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
}

.pcn-confirm__text {
  margin: 10px 0 0;
  font-size: 0.88rem;
  color: var(--dk-muted, #6b6472);
}

.pcn-confirm__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.pcn-confirm__ghost,
.pcn-confirm__primary {
  padding: 9px 18px;
  border-radius: 999px;
  font: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
}

.pcn-confirm__ghost {
  border: 1px solid var(--dk-line, #ece6ef);
  background: transparent;
  color: var(--dk-ink, #231a2e);
}

.pcn-confirm__primary {
  border: 0;
  background: linear-gradient(
    150deg,
    var(--dk-pink, #e8276f),
    var(--dk-grape, #a826e1)
  );
  color: #fff;
}

.pcn-confirm__primary:disabled {
  opacity: 0.6;
  cursor: progress;
}

/* ---------- colunas ---------- */
.pcn-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr);
  gap: 22px;
  margin-top: 22px;
  align-items: start;
}

.pcn-section h2 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--ink);
}

.pcn-section p {
  margin: 2px 0 0;
  font-size: 0.8rem;
  color: var(--muted);
}

.pcn-empty {
  margin: 18px 0 4px;
  font-size: 0.88rem;
  color: var(--muted);
}

/* ---------- caderneta ---------- */
.pcn-book {
  padding: 22px 24px 18px;
  border-radius: 22px;
  border: 1px solid var(--line);
  background: var(--surface);
}

.pcn-lines {
  list-style: none;
  margin: 14px 0 0;
  padding: 0;
}

.pcn-line {
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr);
  gap: 14px;
  padding: 12px 0;
  border-top: 1px dashed var(--line);
}

.pcn-line:first-child {
  border-top: 0;
}

.pcn-line--muted {
  opacity: 0.62;
}

.pcn-stamp {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 48px;
  border-radius: 12px;
  background: var(--paper);
  border: 1px solid var(--line);
  line-height: 1;
}

.pcn-stamp__day {
  font-size: 1.05rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--ink);
}

.pcn-stamp__month {
  margin-top: 3px;
  font-size: 0.66rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--muted);
}

.pcn-line__body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.pcn-line__row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}

.pcn-line__title {
  min-width: 0;
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* O pontilhado que leva o olho do nome ao valor, como numa caderneta. */
.pcn-line__leader {
  flex: 1 1 24px;
  min-width: 24px;
  border-bottom: 2px dotted var(--line);
  transform: translateY(-4px);
}

.pcn-line__amount {
  flex-shrink: 0;
  font-size: 1rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--ink);
}

.pcn-line__amount.is-gain {
  color: var(--gain);
}

.pcn-line__amount.is-loss {
  color: var(--loss);
}

.pcn-line__amount.is-expire {
  color: var(--ember);
}

.pcn-line__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0 4px;
  margin-top: 3px;
  font-size: 0.75rem;
  color: var(--muted);
}

.pcn-line__balance {
  margin-left: auto;
  font-variant-numeric: tabular-nums;
  color: var(--faint);
}

.pcn-line__reason {
  margin-top: 6px;
  padding-left: 10px;
  border-left: 2px solid var(--line);
  font-size: 0.8rem;
  font-style: italic;
  color: var(--muted);
}

.pcn-more {
  display: flex;
  justify-content: center;
  margin-top: 10px;
}

.pcn-more__btn {
  padding: 8px 18px;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: transparent;
  color: var(--grape);
  font: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
}

.pcn-more__btn:hover,
.pcn-more__btn:focus-visible {
  background: var(--paper);
}

.pcn-more__btn:focus-visible {
  outline: 2px solid var(--grape);
  outline-offset: 2px;
}

.pcn-more__btn:disabled {
  opacity: 0.6;
  cursor: progress;
}

/* ---------- como ganhar ---------- */
.pcn-earn {
  padding: 22px 22px 18px;
  border-radius: 22px;
  background: var(--paper);
  border: 1px solid var(--line);
}

.pcn-rules {
  list-style: none;
  margin: 14px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.pcn-rule {
  padding: 14px 16px;
  border-radius: 16px;
  background: var(--surface);
  border: 1px solid var(--line);
}

.pcn-rule__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.pcn-rule__name {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--ink);
}

.pcn-rule__points {
  flex-shrink: 0;
  padding: 2px 10px;
  border-radius: 999px;
  background: rgba(168, 38, 225, 0.1);
  color: var(--grape);
  font-size: 0.85rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.pcn-rule__points--neg {
  background: rgba(198, 47, 61, 0.1);
  color: var(--loss);
}

.pcn-rule__desc {
  margin: 6px 0 0;
  font-size: 0.8rem;
  color: var(--muted);
}

.pcn-rule__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
  margin: 6px 0 0;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--faint);
}

.pcn-rule__meta:empty {
  display: none;
}

.pcn-rule__campaign {
  color: var(--pink);
}

.pcn-limit {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
}

.pcn-limit__bar {
  flex: 1;
  height: 6px;
  border-radius: 999px;
  background: var(--line);
  overflow: hidden;
}

.pcn-limit__fill {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, var(--pink), var(--grape));
  transition: width 0.4s ease;
}

.pcn-limit__text {
  flex-shrink: 0;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--muted);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

/* ---------- telas menores ---------- */
@media (max-width: 960px) {
  .pcn-duo {
    grid-template-columns: minmax(0, 1fr);
  }

  .pcn-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  /* No celular, "como ganhar" vem antes do extrato. */
  .pcn-earn {
    order: -1;
  }
}

@media (max-width: 720px) {
  .pcn-hero {
    grid-template-columns: minmax(0, 1fr);
    padding: 22px 18px 16px;
  }

  .pcn-medal {
    width: 46px;
  }

  .pcn-stats {
    grid-auto-flow: row;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px 0;
  }

  .pcn-stat:nth-child(odd) {
    border-left: 0;
    padding-left: 0;
  }

  .pcn-book,
  .pcn-earn {
    padding: 18px 16px 14px;
  }

  .pcn-line__balance {
    margin-left: 0;
    flex-basis: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .pcn-limit__fill {
    transition: none;
  }
}
</style>
